import React, { useEffect, useState } from "react";
import { Heart, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import ShareBar from "@/components/ShareBar";

const KEY = "hb_pup_vote";
const toUrl = (p) => (p && p.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${p}` : p);
const readVote = (month) => { try { const v = JSON.parse(localStorage.getItem(KEY) || "null"); return v && v.month === month ? v : null; } catch { return null; } };

export default function PupVoteGallery({ data, onChange }) {
  const [vote, setVote] = useState(() => readVote(data.month));
  const [asking, setAsking] = useState(null);
  const [email, setEmail] = useState(() => readVote(data.month)?.email || "");
  const [busy, setBusy] = useState(false);
  const open = data.voting_open;
  const rallyId = new URLSearchParams(window.location.search).get("pup");
  const rally = rallyId ? data.gallery.find((e) => e.id === rallyId) : null;
  useEffect(() => {
    if (rally) document.querySelector(`[data-testid="pup-gallery-${rally.id}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [rally]);

  const submit = async (entry, mail) => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/pup/vote", { entry_id: entry.id, email: mail });
      const next = { month: data.month, entry_id: r.pending ? null : entry.id, email: mail };
      localStorage.setItem(KEY, JSON.stringify(next));
      setVote(next); setAsking(null);
      toast.success(r.message, { duration: 8000 });
      onChange?.();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const heart = (entry) => {
    if (!open) return;
    if (vote?.email) return submit(entry, vote.email);
    setAsking(entry);
  };

  return (
    <div className="bg-white rounded-3xl soft-shadow p-5" data-testid="pup-page-gallery">
      {rally && (
        <div className="mb-4 rounded-2xl bg-[#FF6B9D] text-white p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="pup-rally-banner">
          <img src={toUrl(rally.photo)} alt={rally.pup_name} className="w-14 h-14 rounded-xl object-cover border-2 border-white shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="font-display font-extrabold text-base" style={{ color: "#fff" }} data-testid="pup-rally-title">Rallying for {rally.pup_name}! 💛</p>
            <p className="text-xs text-white/85">{open ? `Tap the heart on ${rally.pup_name} below, then pass the link on.` : "Voting has closed for this month."}</p>
          </div>
          {open && <button type="button" onClick={() => heart(rally)} className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-white text-[#FF6B9D] font-bold text-sm px-4 py-2 hover:scale-105 transition-transform" data-testid="pup-rally-heart"><Heart className="w-4 h-4 fill-current" /> Heart {rally.pup_name}</button>}
          <div className="shrink-0 bg-white/15 rounded-full p-1"><ShareBar compact title={`Heart ${rally.pup_name} for Pup of the Month 💛`} path={`/api/pup/share?pup=${rally.id}`} testid="pup-rally-share" /></div>
        </div>
      )}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h3 className="font-display font-extrabold text-lg text-[#19324A]">{data.month_label} entries</h3>
          <p className="text-xs text-[#19324A]/55" data-testid="pup-vote-hint">
            {open ? "Tap the heart to back your favourite — we email you a one-tap link to confirm, and it's one verified heart per person each month." : "Voting has closed for this month — results are announced on the 1st."}
          </p>
        </div>
        {data.total_votes > 0 && <span className="shrink-0 text-xs font-bold text-[#FF6B9D] inline-flex items-center gap-1" data-testid="pup-total-votes"><Heart className="w-3.5 h-3.5 fill-current" /> {data.total_votes} hearts</span>}
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
        {data.gallery.map((e) => {
          const mine = vote?.entry_id === e.id;
          return (
            <figure key={e.id} className={`relative rounded-2xl ${rally?.id === e.id ? "ring-4 ring-[#FF6B9D] ring-offset-2" : ""}`} data-testid={`pup-gallery-${e.id}`}>
              <a href={e.url} data-testid={`pup-gallery-link-${e.id}`}><img src={toUrl(e.photo)} alt={e.pup_name} className="w-full aspect-square object-cover rounded-2xl" /></a>
              <button type="button" onClick={() => heart(e)} disabled={!open || busy} aria-pressed={mine} aria-label={`Heart ${e.pup_name}`}
                className={`absolute top-1.5 right-1.5 inline-flex items-center gap-1 rounded-full px-2 py-1 text-[11px] font-bold soft-shadow transition-transform hover:scale-110 disabled:hover:scale-100 ${mine ? "bg-[#FF6B9D] text-white" : "bg-white/95 text-[#19324A]"}`}
                data-testid={`pup-vote-${e.id}`}>
                <Heart className={`w-3.5 h-3.5 ${mine ? "fill-current" : ""}`} /> <span data-testid={`pup-votes-${e.id}`}>{e.votes}</span>
              </button>
              <figcaption className="absolute bottom-1 left-1 right-1 bg-white/90 rounded-xl text-[11px] font-bold text-[#19324A] px-2 py-1 truncate">{e.pup_name}</figcaption>
            </figure>
          );
        })}
      </div>
      {asking && (
        <form onSubmit={(ev) => { ev.preventDefault(); submit(asking, email.trim().toLowerCase()); }} className="mt-4 rounded-2xl bg-[#FFF9EE] p-4" data-testid="pup-vote-form">
          <div className="flex items-start justify-between gap-2">
            <p className="text-sm font-bold text-[#19324A]">Heart {asking.pup_name}? Pop in your email so it's one heart per person.</p>
            <button type="button" onClick={() => setAsking(null)} aria-label="Cancel" className="text-[#19324A]/50 hover:text-[#19324A]" data-testid="pup-vote-cancel"><X className="w-4 h-4" /></button>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 mt-2">
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="flex-1 rounded-xl border-2 border-[#19324A]/10 px-3 py-2 text-sm outline-none focus:border-[#FFD93D] bg-white" data-testid="pup-vote-email" />
            <button type="submit" disabled={busy} className="btn-pink !py-2 inline-flex items-center gap-1.5 justify-center" data-testid="pup-vote-submit"><Heart className="w-4 h-4 fill-current" /> {busy ? "Sending…" : "Email me the confirmation link"}</button>
          </div>
          <p className="text-[11px] text-[#19324A]/45 mt-2">Your heart counts once you tap the link we email you. We only use your email to verify one heart per person — no newsletter sign-up.</p>
        </form>
      )}
    </div>
  );
}
